import React, { FC, useEffect, useMemo } from "react";
import Layout from "../../../common/layouts/Layout";
import Navbar from "../../../common/Navbar/Navbar";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import styles from "./MemberPreview.module.scss";
import IMAGES from "../../../../constants/images";
import { CameraAltOutlined } from "@mui/icons-material";
import {
  useGetEcmUserDetailQuery,
  useRemoveUserMutation,
} from "../../../../generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import WithPermission from "../../../common/WithPermission/WithPermission";
import { ROLES, RoutingList } from "../../../../constants";
import Moment from "../../../common/Moment/Moment";
import { downloadFile, getFileName } from "../../../../utils/helper";
import useAppStore from "../../../../states/zustand/appStore";
import { toast } from "react-toastify";

const MemberPreview: FC = () => {
  const { id } = useParams() as any;
  const navigate = useNavigate();
  const [removeECM] = useRemoveUserMutation();
  const { data, loading, error } = useGetEcmUserDetailQuery({
    variables: {
      id,
    },
  });
  const { setLoader } = useAppStore();

  useEffect(() => {
    setLoader(loading);
  }, [loading]);

  const { training, otherTraining } = useMemo(() => {
    const [training, ...otherTraining] = data?.getUserById.trainings || [];
    return { training, otherTraining };
  }, [data]);
  const handleEdit = () => {
    navigate(RoutingList.members.edit.path.replace(":id", id));
  };
  const handleDelete = async () => {
    try {
      let res = await removeECM({
        variables: {
          _id: id,
        },
      });
      if (!res.data) throw null;
      navigate(RoutingList.members.index.path);

      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Could not delete!");
    }
  };
  return (
    <Layout>
      <>
        <Navbar pageTitle="Members" />
        <Container maxWidth={false}>
          <Card>
            <CardContent className={`pd-x-0 pd-y-0`}>
              <div
                className={`pd-x-30 pd-y-30 d-flex align-items-center justify-content-between`}
              >
                <div className={`d-flex align-items-center`}>
                  <button className={`btn btn-link text-dark`}>
                    <i className={`icon-back-arrow mg-r-10 font-size-lg`}></i>
                  </button>
                  <Typography
                    variant={"body1"}
                    component={"p"}
                    className={`font-size-lg font-weight-bold`}
                  >
                    {data?.getUserById?.name}
                  </Typography>
                </div>
                <WithPermission roles={[ROLES.ECS]}>
                  <div>
                    <Button
                      variant={"outlined"}
                      color={"error"}
                      className="mg-r-10 btn-sm"
                      type="button"
                      onClick={handleEdit}
                    >
                      <i className={`icon-edit mg-r-10`}></i>
                      <span>Edit</span>
                    </Button>
                    <Button
                      variant={"outlined"}
                      color={"error"}
                      className="btn-sm"
                      type="button"
                      onClick={handleDelete}
                    >
                      <i className={`icon-trash mg-r-10`}></i>
                      <span>Delete</span>
                    </Button>
                  </div>
                </WithPermission>
              </div>
              <Divider />
              <div className={`pd-x-30 pd-y-30`}>
                <Grid container className="justify-content-center">
                  <Grid item xs={12} lg={10}>
                    <section>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h2"}
                            component={"h2"}
                            className={`mg-b-20`}
                          >
                            1. Member details
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box className={"d-flex align-items-start"}>
                            <Box
                              className={`avatar avatar-circle avatar-90 ${styles.profileBox} mg-r-20 mg-b-10`}
                            >
                              <img
                                src={data?.getUserById.avatar || ""}
                                alt="..."
                              />
                              <IconButton
                                className={styles.fileUpload}
                                color="primary"
                                aria-label="upload picture"
                                component="label"
                              >
                                <input hidden accept="image/*" type="file" />
                                <Box className={styles.cameraIconWrap}>
                                  <CameraAltOutlined />
                                </Box>
                              </IconButton>
                            </Box>
                            <Box>
                              <Typography
                                variant={"h2"}
                                component={"h2"}
                                className={`mg-b-10 font-weight-medium`}
                              >
                                {data?.getUserById.name}
                              </Typography>
                              <Typography
                                variant={"body1"}
                                component={"p"}
                                color={"secondary"}
                                className={`font-weight-semi-bold mg-b-10`}
                              >
                                {data?.getUserById.qualification?.join(", ")}
                              </Typography>
                              <Typography
                                variant={"body1"}
                                component={"p"}
                                color={"secondary"}
                                className={`font-weight-semi-bold `}
                              >
                                {data?.getUserById.rolesInEC}{" "}
                                {data?.getUserById.isAffiliatedToMedanta
                                  ? "(Affiliated to Medanta)"
                                  : null}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </section>
                    <Divider className={"mg-y-20"} />
                    <section>
                      <Typography
                        variant={"h2"}
                        component={"h2"}
                        className={`mg-b-20`}
                      >
                        2. Terms of appointment
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Date of appointment
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment
                              date={data?.getUserById.dateOfAppointment}
                              format={"ll"}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Date of Tenure Completion
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment
                              date={data?.getUserById.dateOFTenureCompletion}
                              format={"ll"}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Signed Confidentiality Agreement
                          </Typography>
                          <div
                            style={{ cursor: "pointer" }}
                            className={`bg-light uploaded-info d-inline-block`}
                            onClick={() =>
                              downloadFile(
                                data?.getUserById.signedConfidentialityAgreement
                              )
                            }
                          >
                            <i className={`icon-paper icon-left`}></i>
                            <Typography
                              component={"p"}
                              className={`text-truncate`}
                            >
                              {getFileName(
                                data?.getUserById.signedConfidentialityAgreement
                              )}
                            </Typography>
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Signed Conflict of Interest
                          </Typography>
                          <div
                            style={{ cursor: "pointer" }}
                            className={`bg-light uploaded-info d-inline-block`}
                            onClick={() =>
                              downloadFile(
                                data?.getUserById.signedConflictOfInterest
                              )
                            }
                          >
                            <i className={`icon-paper icon-left`}></i>
                            <Typography
                              component={"p"}
                              className={`text-truncate`}
                            >
                              {getFileName(
                                data?.getUserById.signedConflictOfInterest
                              )}
                            </Typography>
                          </div>
                        </Grid>
                      </Grid>
                    </section>
                    <Divider className={"mg-y-20"} />
                    <section>
                      <Typography
                        variant={"h2"}
                        component={"h2"}
                        className={`mg-b-20`}
                      >
                        3. Qualifications & Registration
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            CV
                          </Typography>
                          <div
                            style={{ cursor: "pointer" }}
                            className={`bg-light uploaded-info d-inline-block`}
                            onClick={() => downloadFile(data?.getUserById.cv)}
                          >
                            <i className={`icon-paper icon-left`}></i>
                            <Typography
                              component={"p"}
                              className={`text-truncate`}
                            >
                              {getFileName(data?.getUserById.cv)}
                            </Typography>
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Medical Registration Certificate
                          </Typography>
                          <div
                            className={`bg-light uploaded-info d-inline-block`}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              downloadFile(
                                data?.getUserById.medicalRegistrationCertificate
                              )
                            }
                          >
                            <i className={`icon-paper icon-left`}></i>
                            <Typography
                              component={"p"}
                              className={`text-truncate`}
                            >
                              {getFileName(
                                data?.getUserById.medicalRegistrationCertificate
                              )}
                            </Typography>
                          </div>
                        </Grid>
                      </Grid>
                    </section>
                    <Divider className={"mg-y-20"} />
                    <section>
                      <Typography
                        variant={"h2"}
                        component={"h2"}
                        className={`mg-b-20`}
                      >
                        4. Good Clinical Practices
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            GCP Certification
                          </Typography>
                          {data?.getUserById.GCPCertificate?.map((g) => (
                            <div
                              key={g}
                              style={{ cursor: "pointer" }}
                              onClick={() => downloadFile(g)}
                              className={`bg-light uploaded-info d-inline-block mg-r-20 mg-b-10`}
                            >
                              <i className={`icon-paper icon-left`}></i>
                              <Typography
                                component={"p"}
                                className={`text-truncate`}
                              >
                                {getFileName(g)}
                              </Typography>
                            </div>
                          ))}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Date of Certification
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment
                              date={data?.getUserById.dateOfCertification}
                              format={"ll"}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Valid upto
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment
                              date={data?.getUserById.validUpto}
                              format={"ll"}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Next GCP certification due on
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment
                              date={data?.getUserById.nextGCPCertificateDueOn}
                              format={"ll"}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Name of Training
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            {training?.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Date of Training
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment date={training?.date} format={"ll"} />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Certificate
                          </Typography>
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => downloadFile(training?.url)}
                            className={`bg-light uploaded-info d-inline-block`}
                          >
                            <i className={`icon-paper icon-left`}></i>
                            <Typography
                              component={"p"}
                              className={`text-truncate`}
                            >
                              {getFileName(training?.url)}
                            </Typography>
                          </div>
                        </Grid>
                      </Grid>
                    </section>
                    <Divider className={"mg-y-20"} />
                    <section>
                      <Typography
                        variant={"h2"}
                        component={"h2"}
                        className={`mg-b-20`}
                      >
                        5. Other Trainings
                      </Typography>
                      <Grid container spacing={2}>
                        {otherTraining?.map((o, i) => (
                          <Grid key={i} item xs={12} md={6}>
                            <Typography
                              variant={"h4"}
                              component={"h4"}
                              className={`mg-b-6`}
                            >
                              {o.name}
                              Date: <Moment date={o.date} format={"ll"} />
                            </Typography>
                            <div
                              style={{ cursor: "pointer" }}
                              onClick={() => downloadFile(o.url)}
                              className={`bg-light uploaded-info d-inline-block`}
                            >
                              <i className={`icon-paper icon-left`}></i>
                              <Typography
                                component={"p"}
                                className={`text-truncate`}
                              >
                                {getFileName(o.url)}
                              </Typography>
                            </div>
                          </Grid>
                        )) || "None"}
                      </Grid>
                    </section>
                    <Divider className={"mg-y-20"} />
                    <section>
                      <Typography
                        variant={"h2"}
                        component={"h2"}
                        className={`mg-b-20`}
                      >
                        6. Terms of Resignation
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Date of Resignation
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold`}
                          >
                            <Moment
                              date={data?.getUserById.dateOfResignation}
                              format={"ll"}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant={"h4"}
                            component={"h4"}
                            className={`mg-b-6`}
                          >
                            Reason for resignation
                          </Typography>
                          <Typography
                            variant={"body2"}
                            component={"p"}
                            color={"secondary"}
                            className={`font-weight-semi-bold mg-b-6`}
                          >
                            {data?.getUserById.reasonForResignation}
                          </Typography>
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              downloadFile(data?.getUserById.resignLetter)
                            }
                            className={`bg-light uploaded-info d-inline-block`}
                          >
                            <i className={`icon-paper icon-left`}></i>
                            <Typography
                              component={"p"}
                              className={`text-truncate`}
                            >
                              {getFileName(data?.getUserById.resignLetter)}
                            </Typography>
                          </div>
                        </Grid>
                      </Grid>
                    </section>
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Container>
      </>
    </Layout>
  );
};

export default MemberPreview;
